import React, { useState, useEffect } from 'react';
import ApiService from './ApiService';

function RoleModal({ role, permissions, onClose, onSave }) {
  const [formData, setFormData] = useState({
    roleName: '',
    description: '',
    permissionIds: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (role) {
      // Editing existing role
      setFormData({
        roleName: role.roleName || '',
        description: role.description || '',
        permissionIds: role.permissions ? role.permissions.map(p => p.permissionId) : []
      });
    }
  }, [role]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissionIds: prev.permissionIds.includes(permissionId)
        ? prev.permissionIds.filter(id => id !== permissionId)
        : [...prev.permissionIds, permissionId]
    }));
  };

  const handleCategoryToggle = (category) => {
    const categoryPermissions = permissions[category] || [];
    const categoryPermissionIds = categoryPermissions.map(p => p.permissionId);
    const allSelected = categoryPermissionIds.every(id => formData.permissionIds.includes(id));

    if (allSelected) {
      // Deselect all in this category
      setFormData(prev => ({
        ...prev,
        permissionIds: prev.permissionIds.filter(id => !categoryPermissionIds.includes(id))
      }));
    } else {
      // Select all in this category
      setFormData(prev => ({
        ...prev,
        permissionIds: [...new Set([...prev.permissionIds, ...categoryPermissionIds])]
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.roleName.trim()) {
      setError('Role name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (role) {
        // Update existing role
        await ApiService.updateRole(role.roleId, formData);
      } else {
        // Create new role
        await ApiService.createRole(formData);
      }
      
      onSave();
    } catch (err) {
      console.error('Error saving role:', err);
      setError(err.message || 'Failed to save role');
    } finally {
      setLoading(false);
    }
  };

  const isCategoryFullySelected = (category) => {
    const categoryPermissions = permissions[category] || [];
    const categoryPermissionIds = categoryPermissions.map(p => p.permissionId);
    return categoryPermissionIds.length > 0 && categoryPermissionIds.every(id => formData.permissionIds.includes(id));
  };

  const isCategoryPartiallySelected = (category) => {
    const categoryPermissions = permissions[category] || [];
    const categoryPermissionIds = categoryPermissions.map(p => p.permissionId);
    const selectedCount = categoryPermissionIds.filter(id => formData.permissionIds.includes(id)).length;
    return selectedCount > 0 && selectedCount < categoryPermissionIds.length;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-spruce-800 px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white">
            {role ? 'Edit Role' : 'Create New Role'}
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role Name *
                </label>
                <input
                  type="text"
                  name="roleName"
                  value={formData.roleName}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
                  placeholder="e.g., Office Manager"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
                  placeholder="Brief description of this role"
                  rows={2}
                />
              </div>
            </div>

            {/* Permissions */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Permissions</h3>
              
              <div className="space-y-4">
                {Object.keys(permissions).sort().map((category) => {
                  const categoryPerms = permissions[category];
                  const fullySelected = isCategoryFullySelected(category);
                  const partiallySelected = isCategoryPartiallySelected(category);

                  return (
                    <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Category Header */}
                      <div 
                        className="bg-gray-50 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-100"
                        onClick={() => handleCategoryToggle(category)}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={fullySelected}
                            ref={input => {
                              if (input) input.indeterminate = partiallySelected;
                            }}
                            onChange={() => {}} // Handled by div click
                            className="w-5 h-5 text-spruce-600 rounded focus:ring-2 focus:ring-spruce-500"
                          />
                          <span className="font-semibold text-gray-800">{category}</span>
                          <span className="text-sm text-gray-500">
                            ({categoryPerms.filter(p => formData.permissionIds.includes(p.permissionId)).length}/{categoryPerms.length})
                          </span>
                        </div>
                        <svg 
                          className="w-5 h-5 text-gray-400"
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>

                      {/* Permissions List */}
                      <div className="px-4 py-2 space-y-2">
                        {categoryPerms.sort((a, b) => a.name.localeCompare(b.name)).map((permission) => (
                          <label
                            key={permission.permissionId}
                            className="flex items-start gap-3 py-2 px-2 hover:bg-gray-50 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={formData.permissionIds.includes(permission.permissionId)}
                              onChange={() => handlePermissionToggle(permission.permissionId)}
                              className="mt-1 w-4 h-4 text-spruce-600 rounded focus:ring-2 focus:ring-spruce-500"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-700 text-sm">
                                {permission.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {permission.description}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Selected Count */}
            <div className="bg-spruce-50 border border-spruce-200 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 text-spruce-800">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">
                  {formData.permissionIds.length} permission{formData.permissionIds.length !== 1 ? 's' : ''} selected
                </span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t bg-gray-50 px-6 py-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-spruce-600 text-white rounded-lg hover:bg-spruce-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Saving...' : (role ? 'Update Role' : 'Create Role')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RoleModal;